import {
  useToast,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FlexProps,
  Flex,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import {
  GetTasksBySectionDocumentGQLType,
  useBulkArchiveTasksMutation,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { Counter } from 'packages/common-frontend/components/Counter';
import { Typography } from 'packages/common-frontend/components/Typography';

export const BulkArchiveTasks = ({
  taskIds,
  onSubmit = () => {},
  flexProps = {},
}: {
  taskIds: string[];
  onSubmit?: () => void;
  flexProps?: FlexProps;
}): React.ReactElement | null => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    bulkArchiveTasksMutation,
    { loading: bulkArchiveLoading },
  ] = useBulkArchiveTasksMutation({
    refetchQueries: [GetTasksBySectionDocumentGQLType],
  });

  if (taskIds.length === 0) {
    return null;
  }

  return (
    <>
      <Flex direction="row" {...flexProps}>
        <Button
          variant="secondary-muted"
          colorScheme="red"
          onClick={onOpen}
          gap="0.75em"
        >
          Archive
          <Counter count={taskIds.length} />
        </Button>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Archive Tasks</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Typography>
              The selected {taskIds.length} tasks will be archived. This action
              cannot be reversed.
            </Typography>
          </ModalBody>

          <ModalFooter gap="1em">
            <Button
              colorScheme="v2-aurora"
              variant="secondary"
              isDisabled={bulkArchiveLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              colorScheme="red"
              isDisabled={bulkArchiveLoading}
              onClick={async () => {
                try {
                  const result = await bulkArchiveTasksMutation({
                    variables: {
                      taskIds,
                    },
                  });
                  if (!result.data) {
                    throw (
                      result.errors ??
                      Error(
                        `Failed to bulk archive tasks [taskIds: ${taskIds}]`,
                      )
                    );
                  }
                  onSubmit();
                } catch (err) {
                  Sentry.captureException(err);
                  toast({
                    title: 'Failed to archive tasks.',
                    status: 'error',
                    position: 'top',
                  });
                } finally {
                  onClose();
                }
              }}
            >
              Archive Tasks
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
